// eslint-disable-next-line jira/filename-case
import React, { useCallback, type RefCallback, memo, useId, useMemo, forwardRef } from 'react';
import { MenuIdSettings } from '@jira/navigation-apps__constants/src/MenuIdSettings.tsx';
import { graphql, useFragment } from 'react-relay';
import {
	EXPERIENCE_NAVIGATION_TOP_MENU,
	NAVIGATION_ITEM_ID,
	PACKAGE_NAME,
} from '@jira/navigation-apps__atlassian-navigation-ui/src/constants.tsx';
import { MenuLayout } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/menu/layout/index.tsx';
import { MenuSkeletonContent } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/menu/skeleton/content/main.tsx';
import { MenuSkeletonFooter } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/menu/skeleton/footer/main.tsx';
import { TopLevelErrorBoundary } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/error-boundary/index.tsx';
import { useExperienceStart } from '@jira/platform__jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useTopMenus } from '@jira/navigation-apps__common/src/controllers/top-menus/index.tsx';
import { testIdConcat, testIdGenerate } from '@jira/navigation-apps__common/src/utils/test-id.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@jira/platform__entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
import type { TriggerProps } from '@atlaskit/popup/types';
import Section from '@atlaskit/menu/section';
import { Settings as NewSettingsButton } from '@atlaskit/navigation-system/top-nav-items/settings';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import {
	JiraPopup as Popup,
	type JiraPopupComponentProps,
} from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';

import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useNavigationItemAnalytics } from '../../controllers/navigation-item-analytics/main.tsx';
import { settingsMenuEntryPoint } from './menu/settingsMenuEntryPoint.entrypoint.tsx';
import type { SettingsAction_query$key } from './__generated__/SettingsAction_query.graphql';

const SettingsPopupContainer = forwardRef<HTMLDivElement, JiraPopupComponentProps>(
	(
		{
			children,
			shouldRenderToParent,
			shouldFitContainer,
			appearance,
			isReferenceHidden,
			...htmlAttributes
		},
		ref,
	) => (
		<div {...htmlAttributes} ref={ref} role="dialog" aria-modal="true">
			{children}
		</div>
	),
);
SettingsPopupContainer.displayName = 'SettingsPopupContainer';

const settings = defineMessage({
	defaultMessage: 'Settings',
	id: 'atlassian-navigation.settings.menu.settings',
	description: 'Label for the settings menu item in the top navigation bar',
});

export const Settings: React.MemoExoticComponent<
	({
		settingsActionQueryFragment,
	}: {
		settingsActionQueryFragment: SettingsAction_query$key;
	}) => React.JSX.Element
> = memo(
	({
		settingsActionQueryFragment,
	}: {
		settingsActionQueryFragment: SettingsAction_query$key;
	}): React.JSX.Element => {
		return (
			<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.SETTINGS}>
				<SettingsContainer settingsActionQueryFragment={settingsActionQueryFragment} />
			</TopLevelErrorBoundary>
		);
	},
);

function SettingsContainer({
	settingsActionQueryFragment,
}: {
	settingsActionQueryFragment: SettingsAction_query$key;
}) {
	const isAnonymous = useIsAnonymous();

	if (isAnonymous) return null;

	return <SettingsInner settingsActionQueryFragment={settingsActionQueryFragment} />;
}

function SettingsInner({
	settingsActionQueryFragment,
}: {
	settingsActionQueryFragment: SettingsAction_query$key;
}) {
	const isAdmin = useIsAdmin();
	const cloudId = useCloudId();
	const appEditions = useAppEditions();
	const { formatMessage } = useIntl();
	const [isOpen, { toggle: toggleMenu, off: closeMenu }] = useTopMenus(MenuIdSettings);

	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.SETTINGS,
	});

	useExperienceStart({
		experience: EXPERIENCE_NAVIGATION_TOP_MENU,
		experienceId: NAVIGATION_ITEM_ID.SETTINGS,
		analyticsSource: 'atlassian-navigation',
	});

	const onClick = useCallback(() => {
		toggleMenu();
		triggerAnalytics({ shownCommandPalettePrompt: isAdmin });
	}, [isAdmin, toggleMenu, triggerAnalytics]);

	const testIdPrefix = testIdGenerate('secondary-actions', 'settings');

	const id = useId();
	const titleId = `top-navigation-settings-header-${id}-title`;

	const settingsMenuQueryData = useFragment(
		graphql`
			fragment SettingsAction_query on Query {
				# This fragment is actually used by this module, the eslint rule does not pick it up as it is a prop for the entrypoint
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
				...TopNavSettingsMenu_project_query
			}
		`,
		settingsActionQueryFragment,
	);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			isAdmin,
			appEditions,
		}),
		[cloudId, isAdmin, appEditions],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		settingsMenuEntryPoint,
		entryPointParams,
	);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const Trigger = useCallback(
		(triggerProps: Partial<TriggerProps>) => (
			<div
				data-testid={testIdConcat(testIdPrefix, 'menu-trigger')}
				data-vc="atlassian-navigation-settings"
			>
				<NewSettingsButton
					{...triggerProps}
					ref={(element) => {
						triggerRef(element);
						// oxlint-disable-next-line typescript/consistent-type-assertions
						(triggerProps.ref as RefCallback<HTMLElement>)?.(element);
					}}
					onClick={onClick}
					isSelected={isOpen}
					isListItem={!fg('platform_navx_enghealth57224_settings_a11y')}
					interactionName="nav4-topNav-settings"
					label={formatMessage(settings)}
				/>
			</div>
		),
		[formatMessage, isOpen, onClick, testIdPrefix, triggerRef],
	);

	if (__SERVER__) return <Trigger aria-expanded={false} aria-haspopup />;

	return (
		<Popup
			titleId={titleId}
			isOpen={isOpen}
			{...(fg('platform_navx_enghealth57224_settings_a11y')
				? { popupComponent: SettingsPopupContainer }
				: {})}
			content={() => (
				<JiraEntryPointContainer
					id="nav4-topNav-settings"
					entryPointReferenceSubject={entryPointReferenceSubject}
					packageName={PACKAGE_NAME}
					errorFallback="flag"
					fallback={
						<MenuLayout isWide>
							<Section isScrollable>
								<MenuSkeletonContent testIdPrefix={testIdPrefix} />
							</Section>
							<Section hasSeparator>
								<MenuSkeletonFooter testIdPrefix={testIdPrefix} />
							</Section>
						</MenuLayout>
					}
					runtimeProps={{
						testIdPrefix,
						titleId,
						topNavSettingsMenuProjectQueryFragment: settingsMenuQueryData,
					}}
				/>
			)}
			onClose={closeMenu}
			placement="bottom-end"
			messageId="atlassian-navigation.ui.settings.popup"
			messageType="transactional"
			trigger={Trigger}
			label={formatMessage(settings)}
			shouldRenderToParent
		/>
	);
}
